const isMutatingKey = (event) => {
  if (event.ctrlKey || event.metaKey) return false;
  if (event.key === 'Backspace' || event.key === 'Delete') return true;
  return typeof event.key === 'string' && event.key.length === 1;
};

const findKeywordTokens = (value, keywords) => {
  const tokens = [];
  let cursor = 0;
  value.split(',').forEach((segment) => {
    const segStart = cursor;
    const segEnd = cursor + segment.length;
    const trimmed = segment.trim();
    if (trimmed && keywords.includes(trimmed.toUpperCase())) {
      const innerStart = segStart + segment.indexOf(trimmed);
      const innerEnd = innerStart + trimmed.length;
      tokens.push({ start: innerStart, end: innerEnd });
    }
    cursor = segEnd + 1; // +1 for the comma we split on
  });
  return tokens;
};

/**
 * Detects whether a keydown event would mutate a keyword token (e.g. "AMAP",
 * "ALAP") that the cursor is currently inside or selecting, so the caller can
 * block the keystroke and restore the cell instead of producing a mangled
 * value (e.g. "AM8AP"). The `wiped` flag name is preserved for back-compat
 * with existing call sites; semantically it now means "blocked + restored".
 *
 * Token boundary rules:
 *   - Caret strictly between first and last keyword char -> any mutating key blocks
 *   - Caret immediately AFTER keyword (right edge)       -> Backspace blocks
 *   - Caret immediately BEFORE keyword (left edge)       -> Delete blocks
 *   - Selection partially overlapping a keyword token    -> any mutating key blocks
 *   - Selection fully covering a keyword token           -> allowed (deliberate replace)
 *
 * Mutating keys: single printable character (no Ctrl/Meta), Backspace, Delete.
 *
 * @param {KeyboardEvent} event - The keydown event.
 * @param {string} value - The input's current value.
 * @param {number} selectionStart - The input's current selection/caret start.
 * @param {number} selectionEnd - The input's current selection/caret end.
 * @param {string[]} keywords - Allowed keyword tokens to protect (e.g. ['AMAP']).
 * @param {object} [options] - Behavior overrides.
 * @param {boolean} [options.backspaceDeletes] - When true, Backspace at/inside
 * the token deletes it entirely instead of blocking the keystroke.
 * @returns {{ wiped: boolean, newValue?: string, newCursor?: number }} `wiped: false`
 * when the keystroke should proceed normally; otherwise `newValue`/`newCursor` describe
 * the value and caret position to apply after calling `event.preventDefault()`.
 */
const wipeRepKeywordOnMutation = (
  event,
  value,
  selectionStart,
  selectionEnd,
  keywords,
  options = {},
) => {
  if (!isMutatingKey(event)) return { wiped: false };
  if (typeof value !== 'string' || value.length === 0) return { wiped: false };

  const tokens = findKeywordTokens(value, keywords);
  if (tokens.length === 0) return { wiped: false };

  const hasSelection = selectionStart !== selectionEnd;
  const matched = tokens.find((tok) => {
    if (hasSelection) {
      // Only block when the selection partially overlaps the token. A selection
      // that fully covers the token (e.g. double-click or select-all on a cell
      // that just holds "AMAP") is a deliberate whole-value replace — let the
      // browser's normal "type over selection" behavior happen.
      const overlaps = selectionStart < tok.end && selectionEnd > tok.start;
      if (!overlaps) return false;
      const fullyCovers = selectionStart <= tok.start && selectionEnd >= tok.end;
      return !fullyCovers;
    }
    if (event.key === 'Backspace') {
      // Caret strictly inside, or at the right edge (immediately after)
      return selectionStart > tok.start && selectionStart <= tok.end;
    }
    if (event.key === 'Delete') {
      // Caret strictly inside, or at the left edge (immediately before)
      return selectionStart >= tok.start && selectionStart < tok.end;
    }
    // Typing a printable char: only strict interior counts as "in the middle"
    return selectionStart > tok.start && selectionStart < tok.end;
  });

  if (!matched) return { wiped: false };

  // Opt-in: allow Backspace to actually delete the entire keyword token rather
  // than block. Used by editable tables where users need a way to remove an
  // AMAP/ALAP keyword without clearing the cell another way.
  if (options.backspaceDeletes && event.key === 'Backspace') {
    const newValue = value.slice(0, matched.start) + value.slice(matched.end);
    return { wiped: true, newValue, newCursor: matched.start };
  }

  return { wiped: true, newValue: value, newCursor: matched.end };
};

export default wipeRepKeywordOnMutation;
